import { CheckCircle2, RefreshCw, Stethoscope, TriangleAlert } from 'lucide-react'
import { useState } from 'react'

import { Button, ResourceSelectField } from '@krp/ui-kit'

import { QueryState } from '../../../shared/QueryState'
import type { DiagnosticsActions, DiagnosticsVm } from '../model/types'
import '../../../shared/platform.css'

/**
 * Диагностика и версии. Gateway — версия из «О приложении», готовность — его
 * ручка `/api/gateway/v1/ready` (один origin клиента видит шлюз только под
 * `/api/*`); службы
 * Platform/1С/AQSI браузеру напрямую не видны и проверяются по ресурсу
 * (`diagnostics.read`) в карточке ресурса. Глобального выбора ресурса нет:
 * ресурс открывается своим окном с rid.
 */
export function DiagnosticsScreen({ vm, actions }: { vm: DiagnosticsVm; actions: DiagnosticsActions }) {
  const { version, ready } = vm.gateway
  const [resourceId, setResourceId] = useState('')
  return (
    <section aria-labelledby="platform-diagnostics-title" className="platform-diagnostics">
      <header className="platform-resources__toolbar">
        <h2 id="platform-diagnostics-title">Диагностика</h2>
        <Button
          variant="ghost"
          icon={<RefreshCw size={16} aria-hidden="true" />}
          onClick={actions.reload}
          actionId="platform.diagnostics.reload"
        >
          Обновить
        </Button>
      </header>

      <section aria-label="Шлюз">
        <h3>Шлюз (Gateway)</h3>
        <dl>
          <dt>Версия</dt>
          <dd>
            {version.value !== null ? (
              `${version.value.service} ${version.value.version}`
            ) : (
              <QueryState query={version.query} emptyText="Нет данных" onRetry={actions.reload} />
            )}
          </dd>
          <dt>Готовность шлюза</dt>
          <dd role="status">
            {ready.ready === true && (
              <>
                <CheckCircle2 size={16} aria-hidden="true" /> Готов ({ready.status})
              </>
            )}
            {ready.ready === false && (
              <>
                <TriangleAlert size={16} aria-hidden="true" /> Не готов: {ready.status}
              </>
            )}
            {ready.ready === null && ready.query.status === 'loading' && 'Проверяется…'}
            {ready.ready === null && ready.query.status !== 'loading' && ready.query.error !== null && (
              <>
                <TriangleAlert size={16} aria-hidden="true" /> {ready.query.error.message}
              </>
            )}
          </dd>
        </dl>
        <p className="platform-diagnostics__note">
          Готовность — настроен ли шлюз (вход и маршруты к службам). Версии и готовность служб Platform, 1С и AQSI шлюз браузеру не отдаёт — служба ресурса проверяется в его
          карточке.
        </p>
      </section>

      <section aria-label="Ресурсы с диагностикой">
        <h3>Ресурсы, доступные вам для диагностики</h3>
        {vm.resources.query.status !== 'ready' ? (
          <QueryState query={vm.resources.query} emptyText="Ресурсов нет" onRetry={actions.reload} />
        ) : vm.resources.rows.length === 0 ? (
          <p>Нет ресурсов, где диагностика включена и выдана вашей роли</p>
        ) : (
          <>
            <ResourceSelectField
              label="Ресурс"
              value={resourceId}
              options={vm.resources.rows.map((row) => ({ value: row.id, label: row.name, mark: row.kindTitle }))}
              notChosen="— не выбран —"
              onChange={setResourceId}
            />
            <Button
              variant="secondary"
              icon={<Stethoscope size={16} aria-hidden="true" />}
              disabled={resourceId === ''}
              onClick={() => actions.open(resourceId)}
              actionId="platform.diagnostics.open"
              disabledReason={resourceId === '' ? 'Выберите ресурс, диагностику которого открыть' : null}
            >
              Открыть диагностику
            </Button>
          </>
        )}
      </section>
    </section>
  )
}
